/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-else-return */
/* eslint-disable spaced-comment */
/* eslint-disable no-use-before-define */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import URI from 'urijs';
import { Text } from '@fluentui/react';
import { Col, Row } from '@bingads-webui-react/primitive';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { PaymentContext } from '@bingads-webui-cc-react/payment-context';
import { withValidationErrorProvider } from '@bingads-webui-cc-react/validation-error-context';
import AddPIForm from '@bingads-webui-cc-react/smart-add-pi-form';
import { ServerContext } from '@bingads-webui-campaign-react/shared-app-capi';
import MsaAuth from '@bingads-webui-clientcenter/msa-auth-smart';
import { withTelemetry } from './use-telemetry';
import { withClientCenterApiContext } from './clientcenter-api-context';
import { withNotifications } from './notifications';
import { useAccountFetch } from './use-entity';
import useFeatures from './use-features';
import useScope from './use-scope';
import useLazyState from './use-lazy-state';
import TermsAndConditions from './terms-and-conditions';
import { SectionShimmer } from './shimmer';
import usePaymentInfoI18nContext from './payment-info-setup/use-i18n-context';
import usePaymentInfoDataRestrictions from './payment-info-setup/use-data-restrictions';
import usePaymentInfoState from './payment-info-setup/use-state';
import usePaymentInfoSaveHandler from './payment-info-setup/use-save-handler';
import usePaymentSettings from './payment-info-setup/use-payment-settings';
import usePaymentInstrumentEditorProps from './payment-info-setup/use-pi-editor-props';
import useErrorLogs from './payment-info-setup//use-error-logs';

export default withNotifications(withTelemetry('PaymentInfoSetup', withValidationErrorProvider(withClientCenterApiContext(PaymentInfoSetup))));

const EmptyMap = Object.freeze({});

// OVERVIEW: same overall interface as the BusinessInfoSetup component
// dataRestrictions: for each supported field - either the list of supported values, or a function that receives the original list and returns the filtered list
// init: initial values for each supported field; the values can behave as fallbacks/defaults (in case a more suitable saved value does not exist), or as overrides
// onStateChange: callback on major events, e.g. loading done, saving data, etc.
// setSaveHandler: callback to allow the host component to receive the internal function that handles the save operation; usually this will be preserved in a "ref" and invoked when the Next button is clicked
function PaymentInfoSetup({
  className,
  controls,
  renderCustomCouponView,
  init: {
    country,
    paymentType,
    paymentMethod,
    autorecharge,
    amount,
    coupon,
    cfar,
  },
  dataRestrictions: {
    paymentTypes: paymentTypeFilter,
    paymentMethods: paymentMethodFilter,
    countries: countryFilter,
  },
  onStateChange,
  setSaveHandler,
  setLoading,
  setPidlLoading,
  shouldRetryWithPopup,
  shouldAuthenticateWithPopup,
}) {
  const scope = useScope();
  const features = usePaymentInfoFeatures();
  const { i18n, legacyI18n } = usePaymentInfoI18nContext();
  const [serverContext] = useContext(ServerContext);
  const [msaAuthHandled, setMsaAuthHandled] = useState(false);

  const { data: account, error: accountError } = useAccountFetch(scope);

  // TODO: handle loading errors
  useErrorLogs([accountError, 'Account load failed: ']);

  // Nomenclature:
  // paymentType, e.g. Prepay vs Postpay
  // paymentMethod == paymentInstrumentType == piType, e.g. CreditCard vs PayPal
  // paymentInstrument == pi, i.e. an instance of a payment method, a specific credit card, PayPal account etc.

  // NOTE: the currencyMapping prop of AddPIForm is used to find
  // which billing countries are valid based on the selected currency,
  // but here the currency is immutable, so we do the filtering,
  // beforehands, ourselves in dataRestrictions
  const currency = _.result(account, 'CurrencyType');
  const dataRestrictions = usePaymentInfoDataRestrictions(scope, {
    paymentTypes: paymentTypeFilter,
    paymentMethods: paymentMethodFilter,
    countries: countryFilter,
    currency,
  });

  // NOTE: while the account is loading, we just assume it's draft (most common situation when using this component)
  const [isDraft, setIsDraft] = useLazyState(!account || account.LifecycleStatus === Constants.AccountLifecycleStatus.Draft, !!account);
  const activate = useCallback(() => setIsDraft(false), [setIsDraft]);
  // NOTE: if there are any existing PIs, or the account is not draft,
  // the user should have already agreed to the T&C (this can happen on flows that reload the page, e.g. PayPal)
  // NOTE2: for now, we don't support Adding Funds, so override this option regardless of what the host component wants
  const { hideTerms, hideAddFunds } = {
    hideTerms: controls.hideTerms || !_.isEmpty(dataRestrictions.piOptions) || !isDraft,
    hideAddFunds: true,
  };

  const preselectedPiid = dataRestrictions.paymentContext.paymentContext.PaymentInstrumentId;
  const {
    savePiAttempted,
    saveTermsAttempted,
    attachSubmitEvent,
    detachSubmitEvent,
    handlePiAddSuccess,
    onDataChange,
  } = usePaymentInfoSaveHandler({
    scope,
    preselectedPiid,
    setSaveHandler,
    onStateChange,
    hideTerms,
    activate: isDraft ? activate : undefined,
    coupon,
    cfar,
    currency,
  });

  // TODO: disable add funds if "prepay" is not an option
  // TODO: skip if a PI is already associated with the account
  // NOTE: we try to give the preselected PI priority and adjust other settings accordingly,
  // to ensure they are compatible with the PI as much as possible
  const paymentSettings = usePaymentSettings({
    piid: preselectedPiid,
    piOptions: dataRestrictions.piOptions,
    paymentType,
    paymentMethod,
    isLoading: dataRestrictions.isLoading,
  });
  const [state, setState] = usePaymentInfoState({
    account,
    onStateChange: onDataChange,
    initializers: {
      country,
      paymentType: paymentSettings.type,
      paymentMethod: paymentSettings.method,
      piid: paymentSettings.piid,
      amount,
      autorecharge,
      isLoading: paymentSettings.isLoading,
    },
  });

  // NOTE: for now we ignore the "loading" signals from inside the PI editor,
  // as they can occur on and off throughout the lifetime of the component,
  // and can also overlap in meaning with "save" signals
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const { props: { paymentInstrumentEditorProps, offlinePiProps }, isLoading: isEditorLoading } = usePaymentInstrumentEditorProps({
    scope,
    currency,
    hideAddFunds,
    attachSubmitEvent,
    detachSubmitEvent,
    handlePiAddSuccess,
    setLoading,
    setPidlLoading,
  });

  // TODO: when adding funds: EstimatedTaxAmount comes from jsData.EstimateTaxesAllPayments (cc-data-actions), TopUpLimits comes from jsData.TopUpLimit (cc-data-actions)

  const showTermsAndConditions = !hideTerms && isDraft && !state.isLoading;
  // we must have a valid payment method preselected, in order to avoid explicitly having to select that
  const showPiTypeOptions = controls.paymentMethod !== 'hidden' || state.piType === Constants.PaymentInstrumentType.Unset;

  // fix payment method issue for incognito window.
  // for incognito window, actually it's expected to refresh page once for MSA account when triggering the payment method.
  // Due to 3rd party cookie limitation in the incognito window, it cannot simply get the token silently from iframe.
  // So there is a backup mechanism to trigger redirection to get token back with page refreshed.
  // in the backup mechanism, it generates a cookie delegateCookieHash first from API /cc/PaymentMethods/MsaDelegateAuth.
  // Then the handleLoginCookie will process it to msaDelegate cookie for later usage.
  useEffect(() => {
    if (_.isEmpty(serverContext) || _.isEmpty(serverContext.PidlConfiguration) || msaAuthHandled) {
      return;
    }

    const {
      CurrentAccount: {
        Id: accountId,
      },
      CurrentCustomer: {
        Id: customerId,
      },
      CurrentUser: {
        Id: userId,
      },
      PidlConfiguration: {
        MsaDelegateAuthHashCookieName,
        MsaDelegateAuthUrl,
      },
      CookieDomain,
      CookiePath,
    } = serverContext;

    if (MsaDelegateAuthHashCookieName && MsaDelegateAuthUrl && CookieDomain && CookiePath) {
      const msaAuth = new MsaAuth({
        cookieDomain: CookieDomain,
        cookiePath: CookiePath,
        delegateAuthUrl: MsaDelegateAuthUrl,
        customLogoutUrl: window.location.href,
        getCurrentUrl: () => {
          const currentUri = new URI(window.location.href);
          currentUri.addSearch({
            aid: accountId,
            cid: customerId,
            uid: userId,
          });
          return currentUri.href();
        },
      });
      msaAuth.handleLoginCookie(MsaDelegateAuthHashCookieName);
      setMsaAuthHandled(true);
    }
  }, [msaAuthHandled, serverContext]);

  // NOTE: the country deciding T&C is the ABL country (immutable here), NOT the billing country!
  return (
    <div className={classNames(className, 'payment-info-setup')}>
      <Row className="payment-info-form">
        <Col sm={12}>
          <SectionShimmer isDataLoaded={!state.isLoading} count={4}>
            <PaymentContext.Provider value={dataRestrictions.paymentContext}>
              {!state.isLoading && <AddPIForm
                i18n={i18n}
                legacyI18n={legacyI18n}
                cid={scope.cid}
                aid={scope.aid}
                accountNumber={_.result(account, 'Number')}
                address={_.result(account, 'BusinessAddress')}
                country={state.country}
                countryOptions={dataRestrictions.countryOptions}
                setCountry={setState.country}
                piType={state.piType}
                setPiType={setState.piType}
                showPiTypeOptions={showPiTypeOptions}
                paymentType={state.paymentType}
                paymentTypeOptions={dataRestrictions.paymentTypeOptions}
                setPaymentType={setState.paymentType}
                showPaymentTypeOptions={controls.paymentType !== 'hidden'}
                piid={state.piid}
                existingPis={dataRestrictions.piOptions}
                setPiid={setState.piid}
                currency={_.result(account, 'CurrencyType')}
                currencyMapping={EmptyMap}
                paymentAdderOptions={paymentInstrumentEditorProps}
                setOfflinePiAddress={offlinePiProps.setAddress}
                permissions={features}
                setIsValid={setState.piValidation}

                fetchOfflinePiInstructions={offlinePiProps.fetchInstructions}
                paymentAmount={state.amount}
                setPaymentAmount={setState.amount}
                isAutoRechargeOn={state.autorecharge}
                setIsAutoRechargeOn={setState.autorecharge}
                alwaysShowAddFundsAmountError={savePiAttempted}
                isSmartAddFundsUrl={false}
                showCouponCode={false}
                EstimatedTaxAmount={0}
                TopUpLimits={[]}
                SignupAddFundsResult={{}}
                shouldRetryWithPopup={shouldRetryWithPopup}
                shouldAuthenticateWithPopup={shouldAuthenticateWithPopup}
              />}
              {state.piType === Constants.PaymentInstrumentType.Unset && savePiAttempted && paymentMethodRequiredNote({ i18n })}
            </PaymentContext.Provider>
          </SectionShimmer>
        </Col>
      </Row>
      {_.isFunction(renderCustomCouponView) && renderCustomCouponView()}
      {showTermsAndConditions &&
        <TermsAndConditions
          country={_.chain(account).result('BusinessAddress').result('Country').value()}
          onChange={setState.terms}
          showValidationResult={saveTermsAttempted}
        />}
    </div>
  );
}

// extract all the features/pilots required by AddPIForm component
function usePaymentInfoFeatures() {
  const features = useFeatures(
    'Psd2Validation',
    'Modern3ds',
    'MSANPromotionFlow',
    'MSANFacebookPromotionFlow',
    'MSANGooglePromotionFlow',
    'AutoRechargePromotion',
    'PreAuthPostpaidPhase1',
    'JCBCardSupport'
  ).objects;

  return useMemo(() => ({
    dynamic: {
      ..._.mapObject(features, f => f.isEnabled),
    },
  }), [features]);
}

function paymentMethodRequiredNote({ i18n }) {
  return (
    <div className="payment-method-required-note">
      <Text>{i18n.getString(_TL_('A payment method is required'))}</Text>
    </div>
  );
}

const ControlPropType = PropTypes.oneOf(['hidden', 'disabled']);
const InitializerPropType = type => PropTypes.oneOfType([type, PropTypes.shape({
  value: type.isRequired,
  type: PropTypes.oneOf(['default', 'override']),
})]);

const PaymentTypePropType = PropTypes.oneOf([
  Constants.PaymentTypeId.Prepay,
  Constants.PaymentTypeId.Postpay,
]);

const PaymentMethodPropType = PropTypes.oneOf([
  Constants.PaymentInstrumentType.CreditCard,
  Constants.PaymentInstrumentType.PayPal,
  Constants.PaymentInstrumentType.OfflinePaymentMethod,
  Constants.PaymentInstrumentType.ELV,
  Constants.PaymentInstrumentType.Boleto,
  Constants.PaymentInstrumentType.Unset,
]);

PaymentInfoSetup.propTypes = {
  className: PropTypes.string,
  setSaveHandler: PropTypes.func.isRequired,
  onStateChange: PropTypes.func,
  setLoading: PropTypes.func,
  setPidlLoading: PropTypes.func,
  renderCustomCouponView: PropTypes.func,
  shouldAuthenticateWithPopup: PropTypes.bool,
  shouldRetryWithPopup: PropTypes.bool,
  controls: PropTypes.shape({
    hideTerms: PropTypes.bool,
    hideAddFunds: PropTypes.bool,
    paymentType: ControlPropType,
    paymentMethod: ControlPropType,
  }),
  init: PropTypes.shape({
    country: InitializerPropType(PropTypes.string),
    paymentType: InitializerPropType(PaymentTypePropType),
    paymentMethod: InitializerPropType(PaymentMethodPropType),
    autorecharge: InitializerPropType(PropTypes.bool),
    amount: InitializerPropType(PropTypes.number),
    coupon: PropTypes.shape({
      code: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['class', 'code', 'cco', 'ccl']).isRequired,
      website: PropTypes.string.isRequired,
    }),
    cfar: PropTypes.shape({
      businessURL: PropTypes.string,
      businessSegmentL1: PropTypes.string,
    }),
  }),
  dataRestrictions: PropTypes.shape({
    paymentTypes: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PaymentTypePropType)]),
    paymentMethods: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PaymentMethodPropType)]),
    countries: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.string)]),
  }),
};

PaymentInfoSetup.defaultProps = {
  className: null,
  onStateChange: _.noop,
  setLoading: _.noop,
  setPidlLoading: _.noop,
  dataRestrictions: {},
  init: {},
  controls: {},
  renderCustomCouponView: undefined,
  shouldRetryWithPopup: false,
  shouldAuthenticateWithPopup: false,
};
